/**
 * Organization Type Definitions
 * Types for organization state and associated data structures
 */

/** Represents an organization entity */
export interface Organization {
  name: string;
  alias: string;
  created_at: string;
  status: "Active" | "Inactive" | "Suspended";
  description: string;
  tags: Record<string, string>;
  id?: string;
}

/** Expanded row state for organization table */
export interface ExpandedRowsState {
  [organizationId: string]: boolean;
}

/** Organization list page state */
export interface OrganizationListState {
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  expandedRows: ExpandedRowsState;
}

/** Role enum matching API roles */
export enum OrgRole {
  ADMIN = "admin",
  MAINTAINER = "maintainer",
  DEVELOPER = "developer",
  VIEWER = "viewer",
}

/** Role display metadata */
export const ROLE_METADATA: Record<
  OrgRole,
  {
    label: string;
    description: string;
    color: "primary" | "default" | "success" | "warning";
  }
> = {
  [OrgRole.ADMIN]: {
    label: "Admin",
    description: "Full control of the organization and all its resources",
    color: "primary",
  },
  [OrgRole.MAINTAINER]: {
    label: "Maintainer",
    description:
      "Manage projects and deploy stacks, but cannot manage org settings",
    color: "success",
  },
  [OrgRole.DEVELOPER]: {
    label: "Developer",
    description: "View projects, deploy stacks, and manage stack configs",
    color: "default",
  },
  [OrgRole.VIEWER]: {
    label: "Viewer",
    description: "Read-only access to organization, projects, and stacks",
    color: "default",
  },
};

/** Organization member with role information */
export interface OrgMember {
  id: number;
  userId: number;
  role: string;
  status: "Active" | "Invited";
  joinedAt: string;
  createdAt: string;
  permissions: string[];
  user: {
    email: string;
    firstName: string | null;
    lastName: string | null;
  };
}

// ── FinOps Types ────────────────────────────────────────────────────────────

/** Tag schema definition for FinOps cost allocation */
export interface TagSchemaDefinition {
  id: number;
  organizationId: number;
  key: string;
  label: string;
  description: string | null;
  type: "string" | "enum";
  required: boolean;
  allowedValues: string[];
  defaultValue: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Compliance bucket detail */
export interface TagComplianceBucket {
  total: number;
  compliant: number;
}

/** Department within an organization */
export interface Department {
  id: number;
  organizationId: number;
  name: string;
  alias: string;
  teams?: Team[];
  createdAt: string;
  updatedAt: string;
}

/** Team within an organization, optionally under a department */
export interface Team {
  id: number;
  organizationId: number;
  departmentId: number | null;
  name: string;
  alias: string;
  accessLevel?: string;
  memberCount?: number;
  projectCount?: number;
  department?: Department | null;
  createdAt: string;
  updatedAt: string;
}

/** FinOps cost breakdown by team */
export interface CostByTeam {
  teamId: number | null;
  teamName: string;
  teamAlias: string;
  departmentId: number | null;
  mtdCostUsd: string;
}

/** FinOps cost breakdown by department */
export interface CostByDepartment {
  departmentId: number | null;
  departmentName: string;
  departmentAlias: string;
  mtdCostUsd: string;
}

/** FinOps cost breakdown by project */
export interface CostByProject {
  projectId: number | null;
  projectName: string;
  projectAlias: string;
  teamId: number | null;
  tags: Record<string, string>;
  stackCount: number;
  mtdCostUsd: string;
}

/** FinOps cost breakdown by tag value */
export interface CostByTag {
  tagKey: string;
  tagValue: string;
  mtdCostUsd: string;
}

/** FinOps cost breakdown by environment */
export interface CostByEnvironment {
  environment: string;
  mtdCostUsd: string;
}

/** FinOps recommendation */
export interface FinOpsRecommendation {
  title: string;
  subtitle: string;
  potentialMonthlySavingsUsd: string;
}

/** Full FinOps summary */
export interface FinOpsSummary {
  tagCompliancePct: number;
  totalRecords: number;
  taggedRecords: number;
  requiredTags: TagComplianceBucket;
  optionalTags: TagComplianceBucket;
  untaggedResourceCount: number;
  byTeam: CostByTeam[];
  byDepartment: CostByDepartment[];
  byProject: CostByProject[];
  byEnvironment: CostByEnvironment[];
  recommendations: FinOpsRecommendation[];
}

/** Billing payment method */
export interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
  isDefault: boolean;
}

/** Billing forecast summary */
export interface BillingForecast {
  currentMonthUsd: string;
  forecastUsd: string;
  nextPeriodEstimateUsd: string;
}
